'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ListWatchConfig } from '@/lib/backend-api';
import {
  DEFAULT_COMPANY_CRITERIA,
  defaultCompanySubscription,
  useCompanyWatch,
} from '@/hooks/use-company-watch';
import { SubscribeAfterSaveDialog } from '@/new_components/alerts/SubscribeAfterSaveDialog';
import { ListWatchWizard } from '@/new_components/lists/ListWatchWizard';

export type CompanyAlertDialog = 'subscribe' | 'wizard' | null;

/**
 * The two dialogs behind a saved company: the one-click "subscribe to
 * updates?" prompt (shown right after the free save, and from the bell while
 * only saved) and the full alert wizard ("Anpassen", or the bell once
 * subscribed). Shared by the desktop bookmark and the mobile action bar so
 * both follow the same save → subscribe flow.
 */
export function CompanyAlertDialogs({
  euId,
  open,
  onOpenChange,
}: {
  euId: string;
  open: CompanyAlertDialog;
  onOpenChange: (open: CompanyAlertDialog) => void;
}) {
  const { t } = useTranslation();
  const { watch, save, isBusy } = useCompanyWatch(euId);

  // A saved-only watch carries whatever the bookmark stored; the wizard
  // opens on the same defaults the one-click subscribe would use, so the
  // price it shows matches the prompt it was opened from.
  const wizardWatch = useMemo<ListWatchConfig | null>(() => {
    if (watch?.enabled) return watch;
    return {
      ...defaultCompanySubscription(watch),
      criteria: watch?.criteria?.length ? watch.criteria : DEFAULT_COMPANY_CRITERIA,
      enabled: false,
    };
  }, [watch]);

  return (
    <>
      <SubscribeAfterSaveDialog
        open={open === 'subscribe'}
        onOpenChange={(next) => onOpenChange(next ? 'subscribe' : null)}
        kind="company"
        quoteInput={{
          subject_type: 'company',
          eu_id: euId,
          cadence: 'daily',
          criteria: DEFAULT_COMPANY_CRITERIA,
        }}
        onSubscribe={() => save(defaultCompanySubscription(watch))}
        onCustomize={() => onOpenChange('wizard')}
      />

      <ListWatchWizard
        open={open === 'wizard'}
        onOpenChange={(next) => onOpenChange(next ? 'wizard' : null)}
        watch={wizardWatch}
        // A company has no segments, which is what hides Segment Change.
        segmentConfig={null}
        subjectKind="company"
        euId={euId}
        onSave={async (config) => {
          // A 402 throws InsufficientCreditsError; the wizard shows the
          // top-up prompt and stays open.
          await save(config);
          toast.success(
            watch?.enabled
              ? t('watchlist.alertsSaved', 'Alerts updated')
              : t('subscribeAfterSave.subscribed'),
            { position: 'top-center' },
          );
        }}
        onDisable={async () => {
          // Turning updates off keeps the company saved (free); unsaving
          // stays on the bookmark.
          if (!watch) return;
          await save({ ...watch, enabled: false });
          toast.success(t('watchlist.updatesDisabled'), {
            position: 'top-center',
          });
        }}
        isBusy={isBusy}
      />
    </>
  );
}
